import React, { Fragment } from 'react'

import { Stack, Typography, Box, TextField, IconButton, DialogTitle, DialogContent } from '@mui/material'
import { ContentCopy } from '@mui/icons-material'

import { useToast } from '@/contexts/ToastContext'
import { PaymentMethodEnum } from '@/types/Payment'

import { ChargingStepProps } from '../hooks/useStartCharging/types'
import { RotatingMessages } from '../components/RotatingMessages'
import { LoadingPaymentAnimation, PixQRCode } from '../components/PixQRCode'

const WAITING_MESSAGES = [
  'A sessão iniciará após a confirmação',
  'Aguardando a confirmação',
  'Verificando pagamento',
  'Aguarde um momento',
  'Isso pode levar alguns segundos',
]

type PixData = { code: string; image: string }

export const ProcessPaymentStep: React.FC<ChargingStepProps> = ({ startChargingMethods }) => {
  const { state, pixData } = startChargingMethods
  const { show } = useToast()

  const isPixSelected = state.paymentMethod === PaymentMethodEnum.PIX
  const hasPixData = Boolean(pixData?.code && pixData?.image)
  const isPixFlow = isPixSelected || hasPixData

  const title = isPixFlow ? 'Pagamento via Pix' : 'Aguardando pagamento'

  const handleCopyCode = () => {
    if (!pixData?.code) return
    navigator.clipboard.writeText(pixData.code)
    show('Código copiado para a área de transferência!', { severity: 'success' })
  }

  return (
    <Fragment>
      <DialogTitle>
        <Typography variant="h6" fontWeight="bold" component="span">
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} alignItems="center">
          {isPixFlow ? <PixPaymentContent pixData={pixData} onCopyCode={handleCopyCode} /> : <CardPaymentContent />}
        </Stack>
      </DialogContent>
    </Fragment>
  )
}

interface PixPaymentContentProps {
  pixData: PixData | null
  onCopyCode: () => void
}

const PixPaymentContent: React.FC<PixPaymentContentProps> = ({ pixData, onCopyCode }) => {
  if (!pixData) {
    return (
      <Box sx={{ py: 2 }}>
        <LoadingPaymentAnimation />
        <RotatingMessages messages={WAITING_MESSAGES} />
      </Box>
    )
  }

  return (
    <Stack spacing={1.5} width="100%" alignItems="center">
      <Typography variant="body2" color="text.secondary">
        Escaneie o QR Code abaixo ou copie o código para realizar o pagamento. A sessão será iniciada após a
        confirmação.
      </Typography>

      <PixQRCode imgSrc={pixData.image} />

      <TextField
        label="Código Pix"
        value={pixData.code}
        fullWidth
        InputProps={{
          readOnly: true,
          endAdornment: (
            <IconButton onClick={onCopyCode}>
              <ContentCopy />
            </IconButton>
          ),
        }}
      />

      <RotatingMessages messages={WAITING_MESSAGES} />
    </Stack>
  )
}

const CardPaymentContent: React.FC = () => (
  <Box sx={{ py: 2 }}>
    <LoadingPaymentAnimation />
    <RotatingMessages messages={WAITING_MESSAGES} />
    <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 2 }}>
      Aguarde enquanto o seu pagamento é processado
    </Typography>
  </Box>
)
